import './utils';
import Ajv from 'ajv';
import pointer from 'json-pointer';
import _ from 'lodash';

const escape_ptr = val => val.replace('~', '~0').replace('/', '~1');

const unescape_ptr = val => val.replace('~0', '~').replace('~1', '/');

// recursively resolve json pointer references
const resolve_refs = (root, node, path = [], resolved = {}) => {
  if (!node) node = root;
  if (_.isObject(node)) {
    _.each(node, (val, key) => {
      if (_.isString(val) && val.startsWith('#/')) {
        try {
          node[key] = pointer.get(root, val.substr(1));
          resolved[`/${path.concat(key).map(escape_ptr).join('/')}`] = val;
        }
        catch(err) {
          // console.warn(err);
        }
      }
      else if (_.isObject(val)) {
        resolve_refs(root, val, path.concat(key), resolved);
      }
    });
  }
};

const replace_refs = (doc, replacements) => {
  Object.keys(replacements).forEach(key => {
    pointer.set(doc, key, replacements[key]);
  });
  return doc;
};

// recursively strip props
const strip_props = (root, props, node) => {
  if (!node) node = root;
  if (_.isObject(node)) {
    _.each(node, (val, key) => {
      if (props[key]) {
        delete node[key];
      }
      if (_.isObject(val)) {
        strip_props(root, props, val);
      }
    });
  }
};

const wrap_async_resolver = (fn, ctx) => fn ?
  (uri, cb) => fn(uri).then(result => cb(null, result), error => cb(error)) :
  (uri, cb) => cb(`Unable to resolve schema: ${uri}`);

class Validator {
  constructor({schemas, entry, resolver}) {
    const self = this;

    const instance = Ajv({
      jsonPointers: true,
      verbose: true,
      useDefaults: true,
      loadSchema: wrap_async_resolver(resolver, self)
    });

    schemas.forEach(schema => {
      instance.addSchema(schema);
    });

    if (!entry) {
      entry = schemas[0].id;
    }
    const [root, path] = entry.split('#');
    self._instance = instance;
    self._entry = root;
    self._entry_schema = schemas[0];
    self._refmap = {};
    self._engine = null;
    self._current_doc = null;
  }

  async compile(schema) {
    const self = this;
    return new Promise((resolve, reject) => {
      if (_.isString(schema)) {
        schema = {$ref: schema};
      }
      self._instance.compileAsync(schema || self._entry_schema, (err, engine) => {
        if (err) return reject(err);
        resolve(engine);
      });
    });
  }

  lookup(schema_id) {
    return this._instance.getSchema(schema_id);
  }

  static resolve_refs(doc) {
    const resolved = {};
    resolve_refs(doc, doc ? doc.routes : doc, [], resolved);
    return resolved;
  }

  resolve_refs(doc) {
    const refs = Validator.resolve_refs(doc);
    this._refmap = refs;
    return refs;
  }

  replace_refs(doc) {
    return replace_refs(doc, this._refmap);
  }

  static strip_props(doc, props) {
    return strip_props(doc, props);
  }

  strip_props(doc, props) {
    return strip_props(doc, props);
  }

  clear_schema(...args) {
    this._instance.removeSchema(...args);
  }

  async validate_schema(schema, resolver) {
    if (resolver) {
      this._instance._opts.loadSchema = wrap_async_resolver(resolver, this);
    }

    return this.compile(schema);
  }

  async validate(doc, resolver) {
    this._current_doc = doc;
    if (resolver) {
      this._instance._opts.loadSchema = wrap_async_resolver(resolver, this);
    }

    if (!this._engine) {
      this._engine = await this.compile();
    }

    this.resolve_refs(doc);
    const result = this._engine(doc);

    if (this._engine.errors) {
      return false;
    }
    return true;
  }

  resolve_ref(ref, ctx) {
    const [root, ptr] = ref.split('#');
    if (root) {
      ctx = root;
    }
    else {
      [ctx] = ctx.split('#');
    }

    if (!ptr) {
      return {ctx, resolved: this.lookup(ctx).schema || {}};
    }

    try {
      const schema = this.lookup(ctx).schema;
      const resolved = pointer.get(schema, ptr);
      return {ctx: `${ctx}#${ptr}`, resolved};
    }
    catch(err) {
      // TODO: better reference completion
    }

    try {
      const root_schema = this.lookup(this._entry).schema;
      const resolved = pointer.get(root_schema, ptr);
      return {ctx: `${ctx}#${ptr}`, resolved};
    }
    catch(err) {
      // TODO: better reference completion
    }

    return {ctx, resolved: {}};
  }

  get_schemas(schema, path, idx = 0, ctx = null) {
    const self = this;
    if (!ctx) ctx = schema.id;

    // base case - iteration into empty schema is empty
    if (schema === undefined) return [];

    const results = [];
    const ref = schema.$ref;
    if (ref) {
      const res = self.resolve_ref(ref, ctx);

      const chunk = self.get_schemas(res.resolved, path, idx, res.ctx).unique();
      results.extend(chunk);
    }

    if (schema.allOf) {
      const chunk = schema.allOf.map(sub_schema => self.get_schemas(sub_schema, path, idx, `${ctx}/allOf`)).flatten();
      results.extend(chunk);
    }
    if (schema.anyOf) {
      const chunk = schema.anyOf.map(sub_schema => self.get_schemas(sub_schema, path, idx, `${ctx}/anyOf`)).flatten();
      results.extend(chunk);
    }
    if (schema.oneOf) {
      const chunk = schema.oneOf.map(sub_schema => self.get_schemas(sub_schema, path, idx, `${ctx}/oneOf`)).flatten();
      results.extend(chunk);
    }

    // base case - return when path is exhausted
    const lookup = path[idx];
    if (lookup === undefined) {
      schema.__ctx = ctx;
      return results.length > 0 ? results.unique() : [schema];
    }

    switch (schema.type) {
      case 'object': {
        const {properties = {}, patternProperties = {}, additionalProperties} = schema;
        {
          const prop = properties[lookup];
          if (prop) {
            const chunk = self.get_schemas(properties[lookup], path, idx + 1, `${ctx}/properties/${escape_ptr(lookup)}`);
            results.extend(chunk);
          }
          else if (_.isObject(additionalProperties)) {
            const chunk = self.get_schemas(additionalProperties, path, idx + 1, `${ctx}/properties/${escape_ptr(lookup)}`);
            results.extend(chunk);
          }
        }
        {
          const chunk = Object.keys(patternProperties)
            .filter(val => new RegExp(val).test(lookup))
            .map(val => self.get_schemas(patternProperties[val], path, idx + 1, `${ctx}/patternProperties/${escape_ptr(val)}`))
            .flatten();
          results.extend(chunk);
        }
        break;
      }
      case 'array': {
        // for integer keys
        const subscript = parseInt(lookup, 10);
        if (subscript !== NaN) {
          if (schema.maxItems < subscript) {
            break;
          }

          const items = schema.items;
          if (_.isArray(items)) {
            if (subscript < items.length) {
              const chunk = self.get_schemas(items[subscript], path, idx + 1, `${ctx}[${subscript}]`);
              results.extend(chunk);
            }
            else {
              const chunk = self.get_schemas(schema.additionalItems, path, idx + 1, `${ctx}[${subscript}]`);
              results.extend(chunk);
            }
          }
          else {
            const chunk = self.get_schemas(items, path, idx + 1, `${ctx}[${subscript}]`);
            results.extend(chunk);
          }
        }
        break;
      }
    }
    return results.unique();
  }

  allowed_props(path, schema_id) {
    const self = this;
    const schema = self.lookup(schema_id || self._entry).schema;
    const schemas = self.get_schemas(schema, path);
    const results = {
      props: {},
      completions: {},
      values: []
    };
    schemas.forEach(opt => {
      switch (opt.type) {
        case 'object':
          const {properties = {}, patternProperties = {}} = opt;
          _.assign(results.completions, opt.completions);
          Object.keys(properties).forEach(prop => {
            const prop_types = self.get_schemas(opt, [prop], 0, opt.__ctx);
            // TODO: figure out how to handle set of alternatives
            const default_type = prop_types[0];
            results.props[prop] = _.pick(default_type, ['type', 'format', 'description', 'default', 'items', 'completions']);
            const overrides = opt.properties[prop];
            _.assign(results.props[prop], _.pick(overrides, ['description']));
          });
          break;
        default:
          results.values.push(_.pick(opt, ['type', 'format', 'description', 'default', 'items', 'completions', 'enum']));
      }
    });
    return results;
  }

  errors() {
    return (this._engine || this._instance).errors;
  }
}

export default Validator;
